import { ZfDivider, ZfStack, ZfTypography } from '@zafire/react';
import { useFormikContext } from 'formik';
import { CashAccountSource, PortfolioTypeRule } from 'pco-product-service-internal-consumer';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useGetPortfoliosList from '../../../views/PortfolioSummary/hooks/useGetPortfoliosList';
import { PortfolioForm } from '../AddPortfolioDialog';
import { FormSelectField, toLookupOptions } from '../common/FormSelectField';
import { FormTextField } from '../common/FormTextField';
import { useGetDropdown } from '../hooks/useGetDropdown';
import { PortfolioTypeDependentField } from '../portfolioTypeDependentFields';
import { resolveFieldRules } from '../portfolioTypeFieldRules';

type MainCustodyLinkSectionProps = {
  brUid: string;
  isStandaloneCreate: boolean;
  portfolioRules?: readonly PortfolioTypeRule[];
};

type PortfolioWithNumber = { portfolioNumber?: string };

const toMainCustodyPortfolioOptions = (portfolios: PortfolioWithNumber[] = []) =>
  portfolios
    .flatMap((portfolio) => (portfolio.portfolioNumber ? [portfolio.portfolioNumber] : []))
    .map((portfolioNumber) => ({ value: portfolioNumber, label: portfolioNumber }));

export const MainCustodyLinkSection = ({ brUid, isStandaloneCreate, portfolioRules }: MainCustodyLinkSectionProps) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<PortfolioForm>();
  const { data, isLoading } = useGetPortfoliosList(brUid, CashAccountSource.Pco);
  const { portfolioLinkTypeData, isLoading: isLinkTypeLoading } = useGetDropdown();
  const linkTypeOptions = useMemo(() => toLookupOptions(portfolioLinkTypeData ?? []), [portfolioLinkTypeData]);

  const fieldRules = resolveFieldRules({
    rules: portfolioRules ?? [],
    portfolioType: String(values.portfolioType),
    isStandaloneCreate,
    isEdit: false,
  });

  const isVisible = (field: PortfolioTypeDependentField) => fieldRules[field].state !== 'hidden';
  const stateProps = (field: PortfolioTypeDependentField) => ({
    mandatory: fieldRules[field].required === true,
    readonly: fieldRules[field].state === 'disabled',
  });

  const isBrVisible = isVisible('mainCustodyBr');
  const isLinkTypeVisible = isVisible('mainCustodyLinkType');
  const isPortfolioIdVisible = isVisible('mainCustodyPortfolioId');

  if (!isBrVisible && !isLinkTypeVisible && !isPortfolioIdVisible) {
    return null;
  }

  return (
    <>
      <ZfStack direction="row" spacing="small" style={{ marginTop: 'var(--padding-top-sm)' }}>
        <ZfTypography variant="label-medium" color="emphasis" weight="bold">
          {t('pb.portfoliodetail.maincustodylink.title')}
        </ZfTypography>
        <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-xs)' }} />
      </ZfStack>
      <ZfStack direction="row" spacing="small">
        {isBrVisible && (
          <FormTextField
            name="mainCustodyBr"
            label={t('pb.portfoliodetail.maincustodylink.br.label')}
            {...stateProps('mainCustodyBr')}
          />
        )}
        {isLinkTypeVisible && (
          <FormSelectField
            name="mainCustodyLinkType"
            label={t('pb.portfoliodetail.maincustodylink.linktype.label')}
            allowSearch={false}
            isLoading={isLinkTypeLoading}
            options={linkTypeOptions}
            {...stateProps('mainCustodyLinkType')}
          />
        )}
        {isPortfolioIdVisible && (
          <FormSelectField
            name="mainCustodyPortfolioId"
            label={t('pb.portfoliodetail.maincustodylink.portfolioid.label')}
            isLoading={isLoading}
            options={toMainCustodyPortfolioOptions(data?.list)}
            {...stateProps('mainCustodyPortfolioId')}
          />
        )}
      </ZfStack>
    </>
  );
};
